import api from './api';
import type {
  PosStore,
  PosLiveSession,
  PosStaff,
  CreatePosOrderPayload,
  CreatePosOrderResponse,
  PosCustomerSearchResponse,
  PosVariantSearchResult,
  PosVariantDetailResponse,
  PosAvailableSerial,
  PosVariantWarehouseStocksResponse,
  PosGiftVariantOption,
  FulfillmentMode,
  PosBrowseResult,
  PosVariantSearchItem,
  PosComboSearchItem,
  PosStaffSalesChannel,
} from '@/pages/POS/types';
import type { PosServiceEffectiveVatMap } from '@tcr-erp/shared';

export const posService = {
  // TCR-1126 review round 2 — `posChannelOnly` mặc định KHÔNG gửi param, giữ
  // nguyên hành vi cũ (không lọc kênh POS) cho Sửa đơn (Orders/edit.tsx) và
  // Đổi hàng (Exchange/new.tsx) — 2 màn này gọi hàm này KHÔNG truyền tham số
  // thứ 3. CHỈ màn POS (`QRSearchBar` với `posChannelOnly` prop) truyền `true`.
  // TCR-2035 — thêm `storeId` tường minh (optional, cuối cùng) để Omnichat
  // "Tạo đơn hàng" drawer chọn ĐÚNG store của hội thoại thay vì phụ thuộc
  // interceptor session-based (không có ca POS đang mở khi thao tác từ Omnichat).
  // Không truyền ⇒ giữ nguyên hành vi cũ (interceptor auto-inject store của ca).
  searchVariant: (qr: string, fulfillmentType: FulfillmentMode, posChannelOnly?: boolean, storeId?: string) =>
    api
      .get<PosVariantSearchResult>('/pos/variants', {
        params: {
          qr,
          fulfillment_type: fulfillmentType,
          ...(posChannelOnly ? { pos_channel_only: true } : {}),
          ...(storeId ? { store_id: storeId } : {}),
        },
      })
      .then((r) => r.data),

  // TCR-1126 — lưới duyệt sản phẩm/combo ở màn POS. store_id auto-inject bởi
  // interceptor như mọi request /pos/*.
  // TCR-2035 — `storeId` tường minh, cùng lý do như `searchVariant` ở trên.
  // TCR-2303 — `q` tìm kiếm server-side (tên SP/SKU/barcode; BE tự bỏ dấu/hoa,
  // tách từ AND). Trim và CHỈ gửi khi khác rỗng: request trang mặc định không
  // mang param rác, hành vi cũ (không tìm) giữ y nguyên.
  browseVariants: (fulfillmentType: FulfillmentMode, page: number, limit: number, storeId?: string, q?: string) => {
    const search = q?.trim();
    return api
      .get<PosBrowseResult<PosVariantSearchItem>>('/pos/variants/browse', {
        params: {
          fulfillment_type: fulfillmentType,
          page,
          limit,
          ...(storeId ? { store_id: storeId } : {}),
          ...(search ? { q: search } : {}),
        },
      })
      .then((r) => r.data);
  },

  // TCR-2303 — combo thật cũng duyệt được theo `q` (combo_name/combo_sku) để
  // lưới "Chọn từ lưới sản phẩm" hiển thị + tick combo khi đang tìm. Cùng quy
  // ước trim/bỏ-rỗng với `browseVariants` để 2 request song song luôn nhất quán.
  browseCombos: (fulfillmentType: FulfillmentMode, page: number, limit: number, q?: string) => {
    const search = q?.trim();
    return api
      .get<PosBrowseResult<PosComboSearchItem>>('/pos/combos/browse', {
        params: {
          fulfillment_type: fulfillmentType,
          page,
          limit,
          ...(search ? { q: search } : {}),
        },
      })
      .then((r) => r.data);
  },

  // TCR-1362 follow-up — VAT đã resolve của dịch vụ phụ thu/vận chuyển không tự
  // khai vat_rate riêng, để hiển thị đúng số CHO THU NGÂN trước khi checkout
  // (tránh CASH_AMOUNT_MISMATCH khi số resolve khác default tĩnh).
  getServiceEffectiveVat: () =>
    api.get<PosServiceEffectiveVatMap>('/pos/services/effective-vat').then((r) => r.data),

  lookupVariants: (qrCodes: string[], fulfillmentType: FulfillmentMode) =>
    api
      .post<PosVariantDetailResponse>('/pos/variants/lookup', {
        qr_codes: qrCodes,
        fulfillment_type: fulfillmentType,
      })
      .then((r) => r.data),

  // store_id auto-inject bởi interceptor; BE resolve store của ca từ TenantContext.
  listVariantSerials: (variantId: string, params: { q?: string; warehouse_id?: string }) =>
    api
      .get<{ items: PosAvailableSerial[] }>(`/pos/variants/${variantId}/serials`, { params })
      .then((r) => r.data.items),

  // TCR-629: kho của store kèm tồn on-hand của variant (modal chọn kho xuất).
  // storeId tường minh dùng cho màn sửa đơn (store của ĐƠN có thể khác ca hiện tại);
  // bỏ trống → interceptor inject store của ca như mọi request /pos/*.
  // TCR-1205: trả nguyên `{ items, unmatched_available }` (không chỉ `.items`
  // như trước) — `unmatched_available` là tín hiệu để UI cảnh báo khi có tồn
  // kho bị lệch dữ liệu (kho đã xoá) mà thu ngân không thấy trong danh sách.
  listVariantWarehouseStocks: (variantId: string, storeId?: string) =>
    api
      .get<PosVariantWarehouseStocksResponse>(`/pos/variants/${variantId}/warehouse-stocks`, {
        params: storeId ? { store_id: storeId } : undefined,
      })
      .then((r) => r.data),

  // TCR-1074: variant của 1 product kèm tồn kho — dựng dòng "quà tặng" khi áp voucher free_gift.
  listVariantsByProduct: (productId: string, storeId?: string) =>
    api
      .get<{ items: PosGiftVariantOption[] }>(`/pos/variants/by-product/${productId}`, {
        params: storeId ? { store_id: storeId } : undefined,
      })
      .then((r) => r.data.items),

  // TCR-2035 — `storeId` tường minh, cùng lý do như `searchVariant`/`browseVariants`
  // ở trên: Omnichat "Tạo đơn hàng" không có ca POS đang mở nên interceptor session-
  // based không có gì để auto-inject.
  createOrder: (body: CreatePosOrderPayload, storeId?: string) =>
    api
      .post<{ success: boolean; data: CreatePosOrderResponse }>('/pos/orders', body, {
        params: storeId ? { store_id: storeId } : undefined,
      })
      .then((r) => r.data.data),

  confirmCustomerHandover: (orderId: string, body: { note?: string; confirm_outstanding_debt?: boolean } = {}) =>
    api.post(`/pos/orders/${orderId}/fulfillment/customer-handover`, body).then((r) => r.data),
  dispatchFulfillment: (orderId: string, body: Record<string, unknown>) =>
    api.post(`/pos/orders/${orderId}/fulfillment/dispatch`, body).then((r) => r.data),
  confirmShippingAccepted: (orderId: string, body: { note?: string } = {}) =>
    api.post(`/pos/orders/${orderId}/fulfillment/shipping-accepted`, body).then((r) => r.data),
  confirmPod: (orderId: string, body: Record<string, unknown>) =>
    api.post(`/pos/orders/${orderId}/fulfillment/pod`, body).then((r) => r.data),
  appendDeliveryPhotos: (orderId: string, photoUrls: string[]) =>
    api.post(`/pos/orders/${orderId}/fulfillment/pod-photos`, { photo_urls: photoUrls }).then((r) => r.data),
  recordCarrierDelivered: (orderId: string, body: Record<string, unknown>) =>
    api.post(`/pos/orders/${orderId}/fulfillment/carrier-delivered`, body).then((r) => r.data),
  reconcileCarrier: (orderId: string, body: Record<string, unknown>) =>
    api.post(`/pos/orders/${orderId}/fulfillment/carrier-reconcile`, body).then((r) => r.data),

  searchCustomers: (q: string, page = 1, limit = 20) =>
    api
      .get<PosCustomerSearchResponse>('/pos/customers', { params: { q, page, limit } })
      .then((r) => r.data),

  // storeId không cần truyền — axios interceptor auto-inject ?store_id=<sessionStorage>
  // và BE TenantMiddleware resolve từ đó (EG-369). Naming snake_case theo ADR-013.
  // TCR-1576/1577: BE mặc định `limit = 20` (StaffListDto) — gọi trần trụi thì
  // chi nhánh đông nhân viên bị cắt cụt, người dùng tưởng hệ thống lọc theo vai
  // trò. Truyền `limit` (BE cap 100) và `search` để tìm server-side.
  // TCR-2429 — `salesChannel: 'b2b'` đổi phạm vi danh sách: BE bỏ ràng buộc chi nhánh và
  // chỉ trả NVKD GT/MT đang ACTIVE (những người được ghi nhận doanh số trên đơn B2B).
  // Không truyền = giữ nguyên hành vi cũ (NV thuộc store), nên mọi call site B2C không đổi.
  getStaffList: (params?: { limit?: number; search?: string; salesChannel?: PosStaffSalesChannel }) =>
    api
      .get<{ data: PosStaff[] }>('/pos/staff', {
        params: params && {
          limit: params.limit,
          search: params.search,
          ...(params.salesChannel === 'b2b' ? { sales_channel: 'b2b' } : {}),
        },
      })
      .then((r) => r.data.data ?? []),

  getMyStores: () =>
    api.get<{ data: PosStore[] }>('/pos/stores').then((r) => r.data.data ?? []),

  getLiveSessions: () =>
    api
      .get<{ data: PosLiveSession[] }>('/pos/live-sessions')
      .then((r) => r.data.data ?? []),

  voidOrder: (body: { orderId: string; reason: string }) =>
    api.post('/pos/void/order', body).then((r) => r.data),

  voidItem: (body: { orderId: string; itemId: string; reason: string }) =>
    api.post('/pos/void/item', body).then((r) => r.data),

  // Task 5 (BE): endpoint riêng cho thu ngân — chỉ trả provider/label/enabled,
  // không leak token như settings endpoint (Owner-only) mà nó derive từ.
  listShippingCarriers: () =>
    api
      .get<{
        carriers: Array<{
          provider: string;
          label: string;
          enabled: boolean;
          /** Hạn mức COD của hãng. `max = 0` = không chặn trên. */
          cod_limit?: { min: number; max: number };
        }>;
      }>('/shipping/carriers')
      .then((r) => r.data.carriers ?? []),
};
